import type {
	CalculationResult,
	CutRow,
	MaterialPhase,
	MaterialPhaseGroup,
	MaterialRow,
} from "../../engines/types";
import { groupCutRows } from "../purchasing/groupCutsByPhase";

export interface BuildStepSupplies {
	materials: MaterialRow[];
	cuts: CutRow[];
}
// These are working phases, not an exact assembly allocation. Roof/facade and
// insulation/benches share phases; the UI must disclose that wider scope.
const phaseByStep: Partial<Record<string, MaterialPhase>> = {
	foundation: "foundation",
	deck: "deck",
	framing: "framing",
	roof: "roofExterior",
	facade: "roofExterior",
	insulation: "interior",
	benches: "interior",
	systems: "systems",
	drainage: "systems",
	chimney: "systems",
};

export function getBuildStepSupplies(
	stepId: string,
	data: Pick<CalculationResult, "materials" | "cuts"> &
		Partial<Pick<CalculationResult, "materialsByPhase">>,
): BuildStepSupplies | undefined {
	const phase = phaseByStep[stepId];
	if (!phase) return undefined;
	const sourceGroups = data.materialsByPhase ?? [];
	const selectedGroups = sourceGroups.filter((group) => group.phase === phase);
	const materials = selectedGroups.length
		? selectedGroups.flatMap((group) => group.rows)
		: data.materials.filter((row) => row.phase === phase);
	// Include phase-tagged rows in key lookup when the grouped projection is absent.
	const groups: MaterialPhaseGroup[] = [...sourceGroups];
	for (const candidate of [
		"foundation",
		"framing",
		"deck",
		"roofExterior",
		"interior",
		"systems",
	] as const) {
		if (!groups.some((group) => group.phase === candidate))
			groups.push({
				phase: candidate,
				title: "",
				estimatedCostSek: 0,
				rows: data.materials.filter((row) => row.phase === candidate),
			});
	}
	const cuts = groupCutRows(data.cuts, groups)
		.filter((group) => group.id === phase)
		.flatMap((group) => group.rows);
	return materials.length || cuts.length ? { materials, cuts } : undefined;
}
